import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import {Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack} from '@mui/material';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import * as React from 'react';
import {useEffect, useState} from 'react';
import {type CourseApproval} from '@/types/CourseApproval';
import TableSelectedCourseApprovals from '@/components/CourseApproval/TableSelectedCourseApprovals';
import {type MutateFunctionError} from '@/types/MutateFunctionError';
import {useApproveSelectedCourses} from '@/mutations/useApproveSelectedCourses';
import {useUserContext} from '@/components/Providers/UserContext';
import type {MRT_RowSelectionState} from 'material-react-table';

export type ApproveSelectedCoursesModalState = {
    open : boolean;
    title ?: string;
    content ?: string;
    onClose ?: () => void;
};

type Props = {
    selectedCourseApprovals ?: CourseApproval[];
    setRowSelection: (state: MRT_RowSelectionState) => void;
};

const ApproveSelectedCoursesModal = ({selectedCourseApprovals, setRowSelection} : Props) : React.ReactElement => {
    const {user} = useUserContext();
    const [errorMessage, setErrorMessage] = useState('');
    const [modalState, setModalState] = useState<ApproveSelectedCoursesModalState>({open: false});
    const approveSelectedCoursesMutation = useApproveSelectedCourses();
    const approveSelectedCoursesMutationError = approveSelectedCoursesMutation.error as MutateFunctionError;

    const handleApprove = () => {
        if (user && selectedCourseApprovals) {
            const courseYearMaterialApprovalIds: string[] = [];
            const sectionMaterialApprovalIds: string[] = [];

            for (const selectedCourseApproval of selectedCourseApprovals) {
                if (selectedCourseApproval.compoundKeyType === 'Course') {
                    courseYearMaterialApprovalIds.push(selectedCourseApproval.compoundKey);
                }

                if (selectedCourseApproval.compoundKeyType === 'Section') {
                    sectionMaterialApprovalIds.push(selectedCourseApproval.compoundKey);
                }
            }

            approveSelectedCoursesMutation.mutate({
                jwt: user.jwt,
                courseYearMaterialApprovalIds: courseYearMaterialApprovalIds,
                sectionMaterialApprovalIds: sectionMaterialApprovalIds,
            });
        }
    };

    const handleOpen = () => {
        setModalState({
            ...modalState,
            open: true
        });
    };

    const handleClose = () => {
        setModalState({
            ...modalState,
            open: false
        });
    };

    useEffect(() => {
        if (approveSelectedCoursesMutation.isSuccess) {
            const approveSelectedCoursesMutationResponse = approveSelectedCoursesMutation.data;

            if (approveSelectedCoursesMutationResponse.errorCode !== 0) {
                if (approveSelectedCoursesMutationResponse.message !== '') {
                    setErrorMessage(approveSelectedCoursesMutationResponse.message);
                    return;
                }

                setErrorMessage(`Unknown Error: Error Code: ${approveSelectedCoursesMutationResponse.errorCode}`);
                return;
            }

            setRowSelection({});
            setModalState({open: false});
        }
    }, [approveSelectedCoursesMutation.isSuccess]);

    useEffect(() => {
        if (modalState.open) {
            // initialize on open
            setErrorMessage('');
        }
    }, [modalState]);

    return <>
        <Button variant='outlined' onClick={handleOpen}>Approve Selected Courses</Button>
        <Dialog maxWidth='sm' fullWidth={true} open={modalState.open} onClose={handleClose}>
            <DialogTitle>
                <Stack
                    sx={{
                        width: '100%',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    <Stack
                        sx={{
                            width: '100%',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <Typography
                            variant='h6'
                            fontWeight={800}
                            sx={{
                                width: '100%',
                                textAlign: 'center',
                            }}
                        >
                            Approval Confirmation
                        </Typography>
                        <IconButton onClick={handleClose}>
                            <HighlightOffIcon/>
                        </IconButton>
                    </Stack>
                </Stack>
            </DialogTitle>
            <DialogContent sx={{pb: 1, minHeight: '200px'}}>
                {selectedCourseApprovals &&
                    <TableSelectedCourseApprovals selectedCourseApprovals={selectedCourseApprovals}/>}
            </DialogContent>
            <DialogActions sx={{pb: 2, pr: 3}}>
                <Stack
                    sx={{
                        width: '100%',
                        flexDirection: 'column',
                    }}
                >
                    {(approveSelectedCoursesMutation.isError || errorMessage !== '') && <Stack
                        sx={{
                            mb: 2,
                            flexDirection: 'row',
                            justifyContent: 'center',
                        }}
                    >
                        {approveSelectedCoursesMutation.isError && <Alert severity='error'>
                            {approveSelectedCoursesMutationError.message}
                        </Alert>}
                        {errorMessage !== '' && <Alert severity='error'>
                            {errorMessage}
                        </Alert>}
                    </Stack>}
                    <Stack
                        sx={{
                            flexDirection: 'row',
                            justifyContent: 'flex-end',
                        }}
                    >
                        <Button
                            variant='outlined'
                            onClick={() => {
                                setModalState({open: false});
                            }}
                            sx={{
                                mr: 1,
                            }}
                        >
                            No, Cancel
                        </Button>
                        <Button
                            variant='contained'
                            onClick={handleApprove}
                        >
                            Yes, Approve Selected Courses
                        </Button>
                    </Stack>
                </Stack>
            </DialogActions>
        </Dialog>
    </>;
};

export default ApproveSelectedCoursesModal;
